/**
 * Sprint Redesign v1 · Phase 2+ · Baustein 6 (Do 03.09.2026, 19:00).
 *
 * /profil — vier Bereiche + Logout:
 *   1. Profil-Daten oben        (Name · Avatar · E-Mail, read-only)
 *   2. Abo-Status Mitte         (Pro/Free + Manage-Subscription-Link Ablefy Self-Service)
 *   3. Referral-Bereich links   (5-stelliger Code, Freund-einladen, referral_count, Tag-30-Hint)
 *   4. Feedback + Upgrade rechts (Feedback-Button + Upgrade-Card nur Free)
 *   5. Logout-Button am Ende
 *
 * Referral-Daten: users.referral_code + users.referral_count (DB-only Schema, 6 Spalten).
 * Backend liest via /api/user/dashboard (bereits vorhanden).
 * Pricing-Disclosure: nur 39-€-Anker.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, ArrowRight, Copy, CheckCircle2, Users, MessageCircle,
  Sparkles, Info, X, Send, Camera, Pencil, Save, Lock, Mail,
  Trash2, AlertTriangle,
} from "lucide-react";
import RedesignLayout from "@/components/redesign/RedesignLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isPremium } from "@/lib/licensing";
import DeleteAccountModal from "@/components/DeleteAccountModal";

// Sprint V102.b (Di 22.09.2026) — Danger-Zone: Pro-User sehen Hinweis-Box
// (kein Lösch-Button), Free-User sehen den Löschen-Button. Backend liefert
// die Eligibility via GET /api/account/deletion-preview (`eligibility.eligible`).
function DangerZoneSection() {
  const [eligibility, setEligibility] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/account/deletion-preview")
      .then((r) => { if (alive) setEligibility(r.data?.eligibility || null); })
      .catch(() => { if (alive) setEligibility(null); });
    return () => { alive = false; };
  }, []);

  // Fallback: bis Preview geladen, zeigen wir nichts (kein flackerndes UI)
  if (eligibility === null) return null;

  const blocked = eligibility.eligible === false;

  return (
    <>
      <section
        className="mt-8 rounded-2xl border-2 border-red-200 dark:border-red-900/40 bg-red-50/40 dark:bg-red-950/10 p-6"
        data-testid="profil-danger-zone"
        data-eligible={eligibility.eligible ? "true" : "false"}
        data-reason={eligibility.reason || ""}
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold uppercase tracking-widest text-red-600 dark:text-red-400">
              Konto & Daten
            </p>

            {blocked ? (
              <>
                {/* Sprint V102.b — Pro-User: Erst kündigen bei Ablefy */}
                <p
                  className="mt-2 text-sm text-slate-700 dark:text-slate-200 leading-relaxed"
                  data-testid="profil-delete-blocked-message"
                >
                  {eligibility.message}
                </p>
                {eligibility.cancel_url && (
                  <a
                    href={eligibility.cancel_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="profil-delete-cancel-url-btn"
                    className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-white dark:bg-slate-900 border border-red-300 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-950/40 text-red-700 dark:text-red-300 font-bold text-sm px-4 py-2"
                  >
                    Abo bei Ablefy verwalten
                    <ArrowRight className="w-4 h-4" />
                  </a>
                )}
                {eligibility.reason === "lifetime_contact_support" && (
                  <a
                    href="mailto:support@effektiv-kommunizieren.de?subject=Konto-L%C3%B6schung%20Lifetime"
                    data-testid="profil-delete-support-mailto"
                    className="mt-3 ml-2 inline-flex items-center gap-1.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-bold text-sm px-4 py-2"
                  >
                    Support kontaktieren
                  </a>
                )}
              </>
            ) : (
              <>
                {/* Free-User (oder Pro nach Ablauf): normaler Löschen-Button */}
                <p className="mt-1 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                  Du kannst dein Konto jederzeit endgültig löschen — alle Daten werden aus unseren Systemen entfernt, und wir schicken dir keine E-Mails mehr.
                </p>
                <button
                  type="button"
                  onClick={() => setDeleteOpen(true)}
                  data-testid="profil-delete-account-btn"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-red-300 dark:border-red-800 bg-white dark:bg-slate-900 hover:bg-red-100 dark:hover:bg-red-950/40 text-red-700 dark:text-red-300 font-bold text-sm px-4 py-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Konto endgültig löschen
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      <DeleteAccountModal open={deleteOpen} onClose={() => setDeleteOpen(false)} />
    </>
  );
}
// Sprint V11 (Boardroom Sa 05.09.2026-17 Uhr) — FontScaleToggle nicht mehr im
// Profil, nur noch TopBar (siehe TopBarFontScale.jsx). Import auskommentiert.
// import FontScaleToggle from "@/components/redesign/FontScaleToggle";

// Issue 6 — Abo-Variante als lesbares Label (pricing-konform, keine € auf /profil).
function aboVariantLabel(user, isPro) {
  if (!isPro) return "Schnupper-Plan · Kostenlos";
  const tier = user?.license_tier;
  if (tier === "pro_solo_annual") return "Pro Solo · Jährlich";
  if (tier === "pro_solo_monthly") return "Pro Solo · Monatlich";
  if (tier === "power_paket_onetime") return "PowerPaket · Lifetime";
  return "Pro Solo · Aktiv";
}

// Issue 6 — Verlängerungs-Datum lesbar, oder null wenn nicht relevant (Free/Lifetime).
function nextRenewalLabel(user) {
  const tier = user?.license_tier;
  if (tier === "power_paket_onetime" || tier === "free" || !tier) return null;
  const raw = user?.next_billing_date || user?.license_expires_at;
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return null;
  }
}

// Issue 7 + 8 — Persönliche Daten mit Name-Edit und Avatar-Upload (V32).
function ProfilDaten({ user, onUserUpdate }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(user?.first_name || "");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || null);
  const [avatarBlob, setAvatarBlob] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState(null);
  const fileInputRef = React.useRef(null);
  const firstName = user?.first_name || "—";
  const email = user?.email || "—";
  const initials = (user?.first_name || user?.email || "?").split(/[\s@]+/)[0].slice(0, 2).toUpperCase();

  // Sync avatar_url from user prop
  useEffect(() => {
    setAvatarUrl(user?.avatar_url || null);
  }, [user?.avatar_url]);

  // Blob-fetch: img src cannot pass Authorization header, so we proxy via api.get
  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    if (!avatarUrl) { setAvatarBlob(null); return; }
    // avatarUrl starts with /api/ — strip prefix for baseURL
    const relPath = avatarUrl.replace(/^\/api\//, "");
    api.get(relPath, { responseType: "blob" })
      .then((res) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(res.data);
        setAvatarBlob(objectUrl);
      })
      .catch(() => { if (alive) setAvatarBlob(null); });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [avatarUrl]);

  const handleAvatarClick = () => {
    if (avatarUploading) return;
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be picked again
    if (!file) return;
    setAvatarError(null);
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      setAvatarError("Nur JPG, PNG oder WEBP.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setAvatarError("Bild ist zu groß (max. 2 MB).");
      return;
    }
    setAvatarUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/me/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAvatarUrl(data?.avatar_url || null);
      onUserUpdate?.({ ...user, avatar_url: data?.avatar_url || null });
      window.dispatchEvent(new Event("f4s:user-avatar-changed"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setAvatarError(
        typeof detail === "string"
          ? detail.startsWith("too_large")   ? "Bild ist zu groß (max. 2 MB)."
          : detail.startsWith("unsupported") ? "Nur JPG, PNG oder WEBP."
          : "Upload fehlgeschlagen. Bitte erneut versuchen."
          : "Upload fehlgeschlagen. Bitte erneut versuchen.",
      );
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleAvatarDelete = async () => {
    if (avatarUploading) return;
    setAvatarUploading(true);
    setAvatarError(null);
    try {
      await api.delete("/me/avatar");
      setAvatarUrl(null);
      onUserUpdate?.({ ...user, avatar_url: null });
      window.dispatchEvent(new Event("f4s:user-avatar-changed"));
    } catch {
      setAvatarError("Löschen fehlgeschlagen.");
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSave = async () => {
    const value = draft.trim();
    if (!value || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { data } = await api.patch("/user/profile", { first_name: value });
      onUserUpdate?.({ ...user, first_name: data?.first_name || value });
      setEditing(false);
    } catch (e) {
      setSaveError("Speichern fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(user?.first_name || "");
    setSaveError(null);
    setEditing(false);
  };

  return (
    <section
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6"
      data-testid="profil-daten"
    >
      <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)] mb-3">
        Persönliche Daten
      </p>
      <div className="flex items-start gap-4">
        {/* Avatar mit Upload (V32 Emergent Object Storage) */}
        <div className="relative flex-shrink-0" data-testid="profil-avatar-wrapper">
          <div
            className="w-16 h-16 rounded-2xl bg-[var(--fts-primary-bright)] text-white flex items-center justify-center font-extrabold text-xl overflow-hidden"
            data-testid="profil-avatar"
            data-has-image={avatarBlob ? "true" : "false"}
          >
            {avatarBlob ? (
              <img
                src={avatarBlob}
                alt="Profilbild"
                className="w-full h-full object-cover"
                data-testid="profil-avatar-img"
              />
            ) : (
              initials
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleAvatarChange}
            className="hidden"
            data-testid="profil-avatar-file-input"
          />
          <button
            type="button"
            onClick={handleAvatarClick}
            disabled={avatarUploading}
            title={avatarBlob ? "Profilbild ändern" : "Profilbild hochladen"}
            data-testid="profil-avatar-upload-btn"
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 hover:text-[var(--fts-primary-bright)] dark:text-slate-300 flex items-center justify-center shadow-sm disabled:opacity-50"
            aria-label={avatarBlob ? "Profilbild ändern" : "Profilbild hochladen"}
          >
            <Camera className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </div>

        <div className="min-w-0 flex-1">
          {/* Name — Issue 8 Edit */}
          {editing ? (
            <div className="flex items-center gap-2 flex-wrap" data-testid="profil-name-edit">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") handleCancel();
                }}
                data-testid="profil-name-input"
                autoFocus
                maxLength={60}
                className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-lg font-extrabold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--fts-primary-bright)]"
              />
              <button
                type="button"
                onClick={handleSave}
                disabled={!draft.trim() || saving}
                data-testid="profil-name-save-btn"
                className="inline-flex items-center gap-1 rounded-lg bg-[var(--fts-primary-bright)] hover:opacity-90 disabled:opacity-50 text-white font-bold text-xs px-3 py-1.5"
              >
                <Save className="w-3.5 h-3.5" />
                Speichern
              </button>
              <button
                type="button"
                onClick={handleCancel}
                data-testid="profil-name-cancel-btn"
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 font-semibold text-xs px-3 py-1.5"
              >
                Abbrechen
              </button>
              {saveError && (
                <p className="w-full mt-1 text-xs text-red-600 dark:text-red-400" data-testid="profil-name-error">
                  {saveError}
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <p
                className="text-lg font-extrabold text-slate-900 dark:text-white"
                data-testid="profil-name"
              >
                {firstName}
              </p>
              <button
                type="button"
                onClick={() => { setDraft(user?.first_name || ""); setEditing(true); }}
                data-testid="profil-name-edit-btn"
                aria-label="Name bearbeiten"
                className="rounded-lg text-slate-400 hover:text-[var(--fts-primary-bright)] hover:bg-slate-100 dark:hover:bg-slate-800 p-1.5"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* E-Mail read-only (Issue 8) */}
          <div className="mt-1.5 flex items-center gap-1.5" data-testid="profil-email-wrapper">
            <p className="text-xs text-slate-500 dark:text-slate-400 font-mono" data-testid="profil-email">
              {email}
            </p>
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400 dark:text-slate-500"
              title="E-Mail-Änderung nur über Support"
              data-testid="profil-email-readonly-hint"
            >
              <Lock className="w-3 h-3" aria-hidden="true" />
              E-Mail-Änderung über Support
            </span>
          </div>

          {/* V32 — Upload-Hinweise / Fehler-Meldungen */}
          {avatarUploading && (
            <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400" data-testid="profil-avatar-uploading">
              Lade Profilbild hoch …
            </p>
          )}
          {avatarError && (
            <p className="mt-2 text-[11px] font-semibold text-red-600 dark:text-red-400" data-testid="profil-avatar-error">
              {avatarError}
            </p>
          )}
          {!avatarUploading && !avatarError && (
            <p
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 dark:text-slate-400"
              data-testid="profil-avatar-upload-notice"
            >
              <Camera className="w-3 h-3" aria-hidden="true" />
              {avatarBlob ? "JPG · PNG · WEBP · max. 2 MB" : "Profilbild hochladen · JPG/PNG/WEBP · max. 2 MB"}
              {avatarBlob && (
                <button
                  type="button"
                  onClick={handleAvatarDelete}
                  data-testid="profil-avatar-delete-btn"
                  className="ml-2 underline underline-offset-2 hover:text-red-600 dark:hover:text-red-400"
                >
                  Entfernen
                </button>
              )}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function AboStatus({ user, isPro }) {
  const navigate = useNavigate();
  const variantLabel = aboVariantLabel(user, isPro);
  const renewalLabel = nextRenewalLabel(user);
  const isCancelled = user?.license_status === "cancelled";

  return (
    <section
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6"
      data-testid="profil-abo-status"
    >
      <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)] mb-3">
        Abo-Status
      </p>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          {/* Issue 6: Badge mit Variante */}
          <span
            className={[
              "inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-bold uppercase tracking-wide text-xs",
              isPro
                ? "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
                : "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200",
            ].join(" ")}
            data-testid="profil-abo-variant-badge"
          >
            {variantLabel}
          </span>

          {/* Issue 6: Verlängerungs-Datum, wenn relevant */}
          {renewalLabel && (
            <p className="mt-2 text-sm text-slate-700 dark:text-slate-200" data-testid="profil-abo-renewal">
              {isCancelled ? (
                <>Läuft aus am <b className="text-slate-900 dark:text-white">{renewalLabel}</b></>
              ) : (
                <>Nächste Verlängerung: <b className="text-slate-900 dark:text-white">{renewalLabel}</b></>
              )}
            </p>
          )}
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {isPro
              ? "Verwaltung und Kündigung erfolgen direkt bei Ablefy."
              : "Für unbegrenzte Anliegen und Mustererkennung: Pro Solo."}
          </p>
        </div>
        {isPro ? (
          <button
            type="button"
            onClick={() => navigate("/pricing")}
            data-testid="profil-abo-manage-btn"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-100 font-bold text-sm px-4 py-2.5"
          >
            Zur Abo-Verwaltung <ArrowRight className="w-4 h-4" />
          </button>
        ) : null}
      </div>
    </section>
  );
}

function ReferralBox({ user, isPro }) {
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = React.useState(null); // Sprint V1.7 (09.09.2026)
  const code = stats?.referral_code || user?.referral_code || null;
  const count = stats?.referral_count ?? user?.referral_count ?? 0;
  const link = code
    ? `${window.location.origin}/?ref=${encodeURIComponent(code)}`
    : null;

  // Sprint V1.7 Referral-Engine System A (09.09.2026) — Live-Events laden.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/referrals");
        if (!cancelled) setStats(data);
      } catch { /* silent — Widget zeigt user.referral_count als Fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // POSTLAUNCH-V6 · P2-#9 — Deep-Link-Anker aus Bring-a-Friend-Banner
  React.useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#referral") {
      const el = document.getElementById("profil-referral-anchor");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* no-op */ }
  };

  const events = stats?.events || [];
  const paidEvents = events.filter((e) => e.status === "paid");
  const pendingEvents = events.filter((e) => e.status === "pending");

  return (
    <section
      id="profil-referral-anchor"
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6"
      data-testid="profil-referral"
    >
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-[var(--fts-primary-bright)]" aria-hidden="true" />
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
          Referral · Freunde einladen
        </p>
      </div>
      {code ? (
        <>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Dein persönlicher Code</p>
          <p className="text-3xl font-extrabold text-slate-900 dark:text-white font-mono tabular-nums" data-testid="profil-referral-code">
            {code}
          </p>
          {link && (
            <div
              className="mt-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 px-3 py-2 text-xs text-slate-600 dark:text-slate-300 break-all font-mono"
              data-testid="profil-referral-link"
            >
              {link}
            </div>
          )}
          <button
            type="button"
            onClick={handleCopy}
            disabled={!link}
            data-testid="profil-referral-copy-btn"
            className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 text-white font-bold text-sm px-3 py-2"
          >
            {copied ? <><CheckCircle2 className="w-4 h-4" /> Link kopiert</> : <><Copy className="w-4 h-4" /> Link kopieren & teilen</>}
          </button>

          {/* POSTLAUNCH-V6 · P2-#9 — Mechanik + Belohnung klar erklärt */}
          <div
            className="mt-4 space-y-2 text-xs text-slate-600 dark:text-slate-300 leading-snug"
            data-testid="profil-referral-mechanik"
          >
            <div className="flex items-start gap-2">
              <span className="text-[var(--fts-primary-bright)] font-bold flex-shrink-0">1.</span>
              <span>Schick den Link an einen Freund, der Flex4Success noch nicht kennt.</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[var(--fts-primary-bright)] font-bold flex-shrink-0">2.</span>
              <span>Er registriert sich über deinen Link und wird zu deinem geworbenen Neukunden.</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[var(--fts-primary-bright)] font-bold flex-shrink-0">3.</span>
              <span>Sobald er <b className="text-slate-800 dark:text-white">30 Tage im Pro-Solo-Abo</b> ist, bekommst du <b className="text-slate-800 dark:text-white">30 Tage Pro Solo gratis</b>.</span>
            </div>
          </div>

          <p className="mt-4 text-xs text-slate-500 dark:text-slate-400" data-testid="profil-referral-count">
            <b className="text-slate-900 dark:text-white">{count}</b>{" "}
            {count === 1 ? "erfolgreiche Empfehlung" : "erfolgreiche Empfehlungen"}
          </p>

          {/* Sprint V1.7 Referral-Engine (09.09.2026) — Live-Events */}
          {events.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="profil-referral-events">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                Deine letzten Empfehlungen
              </p>
              {pendingEvents.length > 0 && (
                <div className="rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3 py-2 text-xs" data-testid="profil-referral-pending">
                  <b className="text-amber-800 dark:text-amber-300">{pendingEvents.length}</b>{" "}
                  <span className="text-amber-800 dark:text-amber-200">
                    {pendingEvents.length === 1 ? "Empfehlung wartet auf Tag-30-Retention" : "Empfehlungen warten auf Tag-30-Retention"}
                  </span>
                </div>
              )}
              {paidEvents.length > 0 && (
                <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 px-3 py-2 space-y-1.5" data-testid="profil-referral-paid">
                  {paidEvents.slice(0, 3).map((e) => (
                    <div key={e.id} className="text-xs">
                      <p className="text-emerald-800 dark:text-emerald-200">
                        <b>{e.referred_email}</b> · Belohnung ausgezahlt
                      </p>
                      {e.coupon_code && (
                        <p className="mt-0.5 font-mono text-emerald-700 dark:text-emerald-300 text-[11px] tracking-wider">
                          {e.coupon_code}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {isPro && (
            <div className="mt-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 px-3 py-2 flex items-start gap-2" data-testid="profil-referral-tag30">
              <Info className="w-3.5 h-3.5 text-[var(--fts-primary-bright)] mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-snug">
                Tag-30-Mechanik aktiv: Belohnungen greifen, sobald geworbene User <b>30 Tage durchgehend</b> im Pro-Solo-Abo sind.
              </p>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="profil-referral-empty">
          Dein Referral-Code wird bald generiert. Schau in Kürze wieder rein.
        </p>
      )}
    </section>
  );
}

function FeedbackModal({ open, onClose }) {
  const [text, setText] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  if (!open) return null;
  const handleSend = async () => {
    if (!text.trim() || status === "sending") return;
    setStatus("sending");
    try {
      // Best-effort: Backend-Endpoint falls vorhanden, sonst Log-only
      await api.post("/user/feedback", { message: text.trim() }).catch(() => {});
      setStatus("sent");
      setTimeout(() => { setText(""); setStatus("idle"); onClose(); }, 1500);
    } catch {
      setStatus("error");
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="feedback-modal"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">Dein Feedback</p>
            <h2 className="mt-0.5 text-xl font-extrabold text-slate-900 dark:text-white">Was fällt dir auf?</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="feedback-modal-close"
            aria-label="Schließen"
            className="rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 p-2"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          data-testid="feedback-modal-textarea"
          placeholder="Was ist dir aufgefallen? Was fehlt? Was funktioniert nicht?"
          className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm p-3 focus:outline-none focus:ring-2 focus:ring-[var(--fts-primary-bright)]"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!text.trim() || status === "sending"}
          data-testid="feedback-modal-send"
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 disabled:opacity-50 text-white font-bold text-sm px-4 py-2.5"
        >
          {status === "sent" ? (<><CheckCircle2 className="w-4 h-4" /> Danke!</>) : (<><Send className="w-4 h-4" /> Absenden</>)}
        </button>
      </div>
    </div>
  );
}

function FeedbackUpgradeBox({ isPro }) {
  const navigate = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  return (
    <section
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 space-y-4"
      data-testid="profil-feedback-upgrade"
    >
      <div>
        <div className="flex items-center gap-2 mb-2">
          <MessageCircle className="w-4 h-4 text-[var(--fts-primary-bright)]" aria-hidden="true" />
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Feedback
          </p>
        </div>
        <p className="text-sm text-slate-700 dark:text-slate-200 mb-3">
          Sag uns, was gut läuft und was nicht.
        </p>
        <button
          type="button"
          onClick={() => setFeedbackOpen(true)}
          data-testid="profil-feedback-btn"
          className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-100 font-bold text-sm px-3 py-2"
        >
          <MessageCircle className="w-4 h-4" /> Feedback geben
        </button>
      </div>

      {!isPro && (
        <div
          className="rounded-xl border-2 border-[var(--fts-primary-bright)] p-4"
          data-testid="profil-upgrade-card"
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[var(--fts-primary-bright)]" aria-hidden="true" />
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
              Pro Solo starten
            </p>
          </div>
          <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
            Unbegrenzte Anliegen · Mustererkennung · personalisierte Empfehlungen.
          </p>
          <button
            type="button"
            onClick={() => navigate("/pricing")}
            data-testid="profil-upgrade-btn"
            className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 text-white font-bold text-sm px-4 py-2"
          >
            Pro Solo starten <ArrowRight className="w-4 h-4" />
          </button>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Ab 39 €/Monat · Jederzeit kündbar.
          </p>
        </div>
      )}

      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </section>
  );
}

// Sprint V31 (Fr 27.02.2026) — Nurture-Opt-Out-Toggle.
// „Impulse per E-Mail" — DSGVO: legitimes Interesse, jederzeit deaktivierbar.
function NurtureOptOutBox() {
  const [loading, setLoading] = useState(true);
  const [optIn, setOptIn] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/me/nurture");
        if (alive) setOptIn(!!res?.data?.opt_in);
      } catch { /* silent */ } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const toggle = async () => {
    if (saving) return;
    setSaving(true);
    const next = !optIn;
    try {
      await api.post("/me/nurture/toggle", { opt_in: next }, {
        headers: { "Content-Type": "application/json" },
      });
      setOptIn(next);
    } catch { /* silent */ } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6"
      data-testid="profil-nurture-optout"
    >
      <div className="flex items-start gap-3">
        <Mail className="w-5 h-5 text-[var(--fts-primary-bright)] mt-0.5" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Impulse per E-Mail
          </p>
          <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
            {optIn
              ? "Du bekommst 7 kurze Impulse in den ersten 3 Wochen. Kein Spam, kein Verkauf — nur Aha-Momente zu deinem Fokustyp."
              : "Du bist abgemeldet. Dein Zugang zum PocketCoach bleibt aktiv, aber ich schicke dir keine Impuls-Mails mehr."}
          </p>
          <button
            type="button"
            onClick={toggle}
            disabled={loading || saving}
            data-testid="profil-nurture-toggle-btn"
            data-opt-in={optIn ? "true" : "false"}
            className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-100 font-bold text-sm px-3 py-2 disabled:opacity-50"
          >
            {saving ? "Speichern …" : optIn ? "Impulse abbestellen" : "Wieder aktivieren"}
          </button>
        </div>
      </div>
    </section>
  );
}

export default function PageProfil() {
  const navigate = useNavigate();
  const [nameOverride, setNameOverride] = useState(null);
  // Post-Launch-H8 (10.02.2026) — Logout via AuthContext, damit React-State
  // (useAuth) synchron mit den Backend-Cookies geleert wird. Sonst Login-Loop.
  const { logout: authLogout } = useAuth();
  const handleLogout = async () => {
    try { await authLogout(); } catch { /* ignore */ }
    navigate("/login?logout=1", { replace: true });
  };

  return (
    <RedesignLayout>
      {({ data }) => {
        const rawUser = data?.user || {};
        const user = nameOverride ? { ...rawUser, first_name: nameOverride } : rawUser;
        const pro = isPremium(user);
        return (
          <div
            className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6"
            data-testid="page-profil"
            data-tier={pro ? "pro" : "free"}
          >
            <header>
              <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
                Mein Profil
              </p>
              <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                Dein Account
              </h1>
            </header>

            <ProfilDaten
              user={user}
              onUserUpdate={(u) => setNameOverride(u.first_name)}
            />
            <AboStatus user={user} isPro={pro} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ReferralBox user={user} isPro={pro} />
              <FeedbackUpgradeBox isPro={pro} />
            </div>

            <NurtureOptOutBox />

            {/* Sprint V11 (Boardroom-CoS Sa 05.09.2026-17 Uhr) — Cut 2:
                FontScaleToggle hier entfernt. TopBar hat bereits einen Toggle
                (<TopBarFontScale />) — doppelter Kontrollpunkt raus. */}

            <section className="pt-2">
              <button
                type="button"
                onClick={handleLogout}
                data-testid="profil-logout-btn"
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 font-semibold text-sm px-4 py-2.5"
              >
                <LogOut className="w-4 h-4" />
                Ausloggen
              </button>
            </section>

            {/* Sprint V103 (Mo 21.09.2026) — DSGVO Art. 17 Konto-Löschung.
                Sprint V102.b (Di 22.09.2026) — Pro-User-Guard: Danger-Zone
                zeigt statt Löschen-Button einen Hinweis, dass zuerst das
                Ablefy-Abo gekündigt werden muss. Free-User sehen den Button
                wie bisher. Eligibility wird vom Backend geliefert. */}
            <DangerZoneSection />
          </div>
        );
      }}
    </RedesignLayout>
  );
}
